/**
 * Displays ingestion run history for a study with review workflow.
 */

import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import ColumnLayout from "@cloudscape-design/components/column-layout"
import Container from "@cloudscape-design/components/container"
import Header from "@cloudscape-design/components/header"
import Link from "@cloudscape-design/components/link"
import Select, { type SelectProps } from "@cloudscape-design/components/select"
import SpaceBetween from "@cloudscape-design/components/space-between"
import StatusIndicator, {
  type StatusIndicatorProps,
} from "@cloudscape-design/components/status-indicator"
import Table, { type TableProps } from "@cloudscape-design/components/table"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import type { IngestionRun } from "@/services/ingestionApi"
import Pagination from "@/components/Pagination/Pagination"
import { useRunsList, useRunMetrics, usePrefetchRun } from "@/hooks/queries/useIngestion"
import { runTypeLabel } from "@/utils/runLabels"

interface RunHistoryPanelProps {
  studyId: string
}

const STATUS_OPTIONS: SelectProps.Option[] = [
  { label: "All Statuses", value: "" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Failed", value: "FAILED" },
  { label: "Running", value: "RUNNING" },
]

const REVIEW_OPTIONS: SelectProps.Option[] = [
  { label: "All Reviews", value: "" },
  { label: "Pending Review", value: "PENDING_REVIEW" },
  { label: "Acknowledged", value: "ACKNOWLEDGED" },
  { label: "Resolved", value: "RESOLVED" },
  { label: "Ignored", value: "IGNORED" },
]

const STATUS_INDICATOR: Record<string, StatusIndicatorProps.Type> = {
  COMPLETED: "success",
  FAILED: "error",
  RUNNING: "in-progress",
  PARTIALLY_COMPLETED: "warning",
}

const REVIEW_INDICATOR: Record<string, StatusIndicatorProps.Type> = {
  PENDING_REVIEW: "warning",
  ACKNOWLEDGED: "info",
  RESOLVED: "success",
  IGNORED: "stopped",
}

const RunHistoryPanel = ({ studyId }: RunHistoryPanelProps) => {
  const navigate = useNavigate()
  const {
    data,
    isPending: loading,
    error,
    hasNextPage: queryHasNextPage,
    fetchNextPage,
  } = useRunsList(studyId)
  const { data: metrics } = useRunMetrics(studyId)
  const prefetchRun = usePrefetchRun(studyId)

  const [statusFilter, setStatusFilter] = useState("")
  const [reviewFilter, setReviewFilter] = useState("")
  const [currentPage, setCurrentPage] = useState(1)

  const itemsPerPage = 20
  const allRuns = data?.pages.flatMap((page) => page.runs ?? []) ?? []

  const filteredRuns = allRuns.filter((run) => {
    if (statusFilter && run.status.toUpperCase() !== statusFilter) return false
    if (reviewFilter && run.reviewStatus !== reviewFilter) return false
    return true
  })

  const pageCount = Math.ceil(filteredRuns.length / itemsPerPage)
  const paginatedRuns = filteredRuns.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  )

  const handleNextPage = () => {
    const nextPage = currentPage + 1
    const needsMoreData = nextPage * itemsPerPage > filteredRuns.length && queryHasNextPage
    if (needsMoreData) {
      void fetchNextPage()
    }
    setCurrentPage(nextPage)
  }

  const formatReviewStatus = (status?: string) => {
    if (!status) return "—"
    return status
      .replace("_", " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase())
  }

  const handleRunClick = (run: IngestionRun) => {
    navigate(`/studies/${studyId}/runs/${run.runId}`)
  }

  const columnDefinitions: TableProps.ColumnDefinition<IngestionRun>[] = [
    {
      id: "runId",
      header: "Run ID",
      cell: (run) => (
        <span onMouseEnter={() => prefetchRun(run.runId)}>
          <Link
            href={`/studies/${studyId}/runs/${run.runId}`}
            onFollow={(e) => {
              e.preventDefault()
              handleRunClick(run)
            }}
          >
            {run.runId.slice(0, 8)}
          </Link>
        </span>
      ),
    },
    {
      id: "type",
      header: "Type",
      cell: (run) => (
        <Badge color={run.dryRun ? "grey" : "green"}>{runTypeLabel(run.dryRun)}</Badge>
      ),
    },
    {
      id: "dta",
      header: "DTA",
      cell: (run) => run.dtaName || "—",
    },
    {
      id: "dtaVersion",
      header: "DTA Version",
      cell: (run) => run.dtaVersion || "—",
    },
    {
      id: "status",
      header: "Status",
      cell: (run) => {
        const type = STATUS_INDICATOR[run.status.toUpperCase()]
        return type ? <StatusIndicator type={type}>{run.status}</StatusIndicator> : run.status
      },
    },
    {
      id: "review",
      header: "Review",
      cell: (run) => {
        if (!run.reviewStatus) return "—"
        const type = REVIEW_INDICATOR[run.reviewStatus]
        const label = formatReviewStatus(run.reviewStatus)
        return type ? <StatusIndicator type={type}>{label}</StatusIndicator> : label
      },
    },
    {
      id: "failed",
      header: "Failed",
      cell: (run) => run.quarantineSummary?.totalFailed ?? "—",
    },
    {
      id: "file",
      header: "File",
      cell: (run) => run.fileName || "—",
    },
    {
      id: "assigned",
      header: "Assigned",
      cell: (run) => run.assignedTo || "—",
    },
    {
      id: "created",
      header: "Created",
      cell: (run) => new Date(run.createdAt).toLocaleDateString(),
    },
  ]

  return (
    <Container
      header={
        <Header counter={loading ? undefined : `(${filteredRuns.length})`}>Run History</Header>
      }
    >
      <SpaceBetween size="l">
        {metrics && (
          <ColumnLayout columns={4} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Total Runs</Box>
              <Box variant="awsui-value-large">{metrics.totalRuns}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Passed</Box>
              <Box variant="awsui-value-large" color="text-status-success">
                {metrics.passedRuns}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Failed</Box>
              <Box variant="awsui-value-large" color="text-status-error">
                {metrics.failedRuns}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Pending Review</Box>
              <Box variant="awsui-value-large" color="text-status-warning">
                {metrics.pendingReviewCount ?? 0}
              </Box>
            </div>
          </ColumnLayout>
        )}

        <Table
          variant="embedded"
          columnDefinitions={columnDefinitions}
          items={paginatedRuns}
          loading={loading}
          loadingText="Loading runs..."
          trackBy="runId"
          onRowClick={({ detail }) => handleRunClick(detail.item)}
          filter={
            <SpaceBetween direction="horizontal" size="xs">
              <Select
                selectedOption={
                  STATUS_OPTIONS.find((o) => o.value === statusFilter) ?? STATUS_OPTIONS[0]
                }
                onChange={({ detail }) => {
                  setStatusFilter(detail.selectedOption.value ?? "")
                  setCurrentPage(1)
                }}
                options={STATUS_OPTIONS}
                ariaLabel="Filter by status"
              />
              <Select
                selectedOption={
                  REVIEW_OPTIONS.find((o) => o.value === reviewFilter) ?? REVIEW_OPTIONS[0]
                }
                onChange={({ detail }) => {
                  setReviewFilter(detail.selectedOption.value ?? "")
                  setCurrentPage(1)
                }}
                options={REVIEW_OPTIONS}
                ariaLabel="Filter by review status"
              />
            </SpaceBetween>
          }
          empty={
            <Box textAlign="center" color="inherit">
              {error ? error.message : "No ingestion runs match the current filters."}
            </Box>
          }
          pagination={
            filteredRuns.length > 0 ? (
              <Pagination
                currentPage={currentPage}
                pageCount={pageCount}
                hasMore={queryHasNextPage}
                loading={loading}
                onNext={handleNextPage}
                onPrevious={() => setCurrentPage((p) => Math.max(1, p - 1))}
                onPageChange={setCurrentPage}
              />
            ) : undefined
          }
        />
      </SpaceBetween>
    </Container>
  )
}

export default RunHistoryPanel
